import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { ReviewOption } from './cardReviewStorage';

type Measure = { selector: string; name: string };

function freezeEvent(event: { target: EventTarget | null; preventDefault: () => void; stopPropagation: () => void }) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('button');
  if (button) {
    const text = `${button.textContent ?? ''} ${button.getAttribute('aria-label') ?? ''}`;
    if (/\bShow less\b|\+\s*\d+\s+more/i.test(text)) return;
  }
  if (!target.closest('a, button')) return;
  event.preventDefault();
  event.stopPropagation();
}

export function FrozenPreview({ children }: { children: ReactNode }) {
  return (
    <div
      onClickCapture={freezeEvent}
      onAuxClickCapture={freezeEvent}
      onKeyDownCapture={(event) => {
        if (event.key !== 'Enter') return;
        freezeEvent(event);
      }}
    >
      {children}
    </div>
  );
}

function Frame({
  label,
  frame,
  width,
  measure,
  watchTitle,
  children,
}: {
  label: string;
  frame: string;
  width?: number;
  measure?: Measure[];
  watchTitle?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const measuresRef = useRef(measure);
  measuresRef.current = measure;
  const [hits, setHits] = useState<{ name: string; height: number }[]>([]);
  const [titleWidth, setTitleWidth] = useState<number | null>(null);
  const [windowWidth, setWindowWidth] = useState<number | null>(null);
  const measureKey = measure?.map((item) => `${item.selector}|${item.name}`).join(';') ?? '';

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const read = () => {
      setWindowWidth(window.innerWidth);
      const specs = measuresRef.current ?? [];
      setHits(
        specs.flatMap(({ selector, name }) => {
          const el = root.querySelector(selector);
          if (!el) return [];
          return [{ name, height: Math.round(el.getBoundingClientRect().height) }];
        })
      );
      if (watchTitle) {
        const title = root.querySelector('[data-event-title]');
        setTitleWidth(title ? Math.round(title.getBoundingClientRect().width) : null);
      }
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(root);
    window.addEventListener('resize', read);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [measureKey, watchTitle]);

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</p>
      <div
        ref={ref}
        data-frame={frame}
        className="border border-dashed border-gray-300 rounded-lg p-4 overflow-hidden"
        style={width ? { width, maxWidth: '100%' } : undefined}
      >
        <FrozenPreview>{children}</FrozenPreview>
      </div>
      {hits.map((hit) => (
        <p key={hit.name} className="mt-2 text-sm text-gray-600" data-hit-readout={hit.name}>
          {hit.name} is {hit.height}px tall.
          {hit.height < 44 ? ' The product target for a control is 44px.' : ''}
        </p>
      ))}
      {watchTitle && titleWidth != null && windowWidth != null && (
        <p className="mt-2 text-sm text-gray-600" data-title-width={frame}>
          Window {windowWidth}px. Title in this frame is {titleWidth}px wide.
        </p>
      )}
    </div>
  );
}

export type ReviewFrame = 'desktop' | 'narrow';

export function columnCard(frame: ReviewFrame, children: ReactNode) {
  return <div className={frame === 'desktop' ? 'max-w-xs' : undefined}>{children}</div>;
}

export function DualFrame({
  render,
  measure,
  watchTitle,
}: {
  render: (frame: ReviewFrame) => ReactNode;
  measure?: Measure[];
  watchTitle?: boolean;
}) {
  return (
    <div className="space-y-6">
      <Frame label="Desktop" frame="desktop" measure={measure} watchTitle={watchTitle}>
        {render('desktop')}
      </Frame>
      <Frame label="Narrow, 390px" frame="narrow" width={390} measure={measure} watchTitle={watchTitle}>
        {render('narrow')}
      </Frame>
    </div>
  );
}

export function Specimen({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</p>
      <FrozenPreview>{children}</FrozenPreview>
    </div>
  );
}

export function FamilyBlock({
  id,
  title,
  judge,
  destination,
  notice,
  error,
  children,
}: {
  id: string;
  title: string;
  judge: string;
  destination: string;
  notice: string | null;
  error: string | null;
  children: ReactNode;
}) {
  return (
    <section id={id} className="border-t border-gray-200 pt-10 mt-10" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{title}</h2>
      <p className="mt-3 text-gray-700 max-w-3xl">
        <span className="font-medium text-gray-900">What to judge. </span>
        {judge}
      </p>
      <p className="mt-2 text-sm text-gray-600">{destination}</p>
      {notice && <p className="mt-3 text-sm text-gray-700 max-w-3xl">{notice}</p>}
      {error && <p className="mt-3 text-sm text-error max-w-3xl">{error}</p>}
      <div className="mt-8 space-y-10">{children}</div>
    </section>
  );
}

export function ExampleSwitcher({
  exampleId,
  label,
  options,
  choice,
  onChoose,
  note,
  onNote,
  render,
}: {
  exampleId: string;
  label: string;
  options: ReviewOption[];
  choice: string;
  onChoose: (optionId: string) => void;
  note: string;
  onNote: (note: string) => void;
  render: (optionId: string, frame: ReviewFrame) => ReactNode;
}) {
  const shown = choice || 'current';
  const sentence = options.find((option) => option.id === shown)?.sentence ?? '';
  return (
    <div data-example={exampleId} data-choice={choice || 'unset'} className="pt-2">
      <h3 className="text-xl">{label}</h3>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={`Options for ${label}`}>
        {options.map((option) => {
          const pressed = choice === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={pressed}
              onClick={() => onChoose(option.id)}
              className={`min-h-[44px] px-4 rounded-md text-sm font-medium ${
                pressed ? 'bg-primary text-white' : 'border-2 border-primary text-primary bg-white'
              }`}
            >
              {option.name}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-sm text-gray-600 max-w-3xl">{sentence}</p>
      {!choice && <p className="mt-1 text-sm text-gray-500">Not chosen yet. The preview is the current layout.</p>}
      <div className="mt-4">
        <DualFrame render={(frame) => render(shown, frame)} />
      </div>
      <label className="block mt-4 max-w-3xl" htmlFor={`${exampleId}-note`}>
        <span className="block text-sm font-medium text-gray-700">Note for this place</span>
        <textarea
          id={`${exampleId}-note`}
          value={note}
          onChange={(event) => onNote(event.target.value)}
          className="mt-2 w-full min-h-[5.5rem] px-4 py-3 border border-gray-300 rounded-md text-gray-900"
        />
      </label>
    </div>
  );
}

export function linkDump(excerpt: string | null | undefined) {
  return Boolean(excerpt && /https?:\/\/|www\./i.test(excerpt));
}
